import React, { useEffect, useState } from "react";
import {
  SearchOutlined,
  RocketOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ArrowLeftOutlined
} from "@ant-design/icons";
import { Button, Input, Spin, Tabs, Typography, theme } from "antd";
import AddTicket from "./AddTicket";
import axios from "axios";
import GeneralTicketTable from "./GeneralTicketTable";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Error } from "../../../commons/Messages/Messages";
import PageToolbar from "../../../design-system/PageToolbar";
import "./ticket.css";

import { useTranslation } from "react-i18next";
const { Text } = Typography;

const formatTime = totalSeconds => {
  if (!totalSeconds) return "0s";
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${h > 0 ? h + "h " : ""}${m > 0 ? m + "m " : ""}${s}s`;
};

const authHeaders = () => ({ Authorization: "Bearer " + localStorage.getItem("token") });

const Tickets = () => {
  const { t } = useTranslation("dashboard");
  const { token } = theme.useToken();
  const { id } = useParams();
  const history = useHistory();
  const { pathname } = useLocation();
  const [groups, setGroups] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [board, setBoard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [activeKey, setActiveKey] = useState();

  // Same page under /user and /client.
  const boardsPath = `/${pathname.split("/")[1]}/Boards`;

  const load = async (url, setter) => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}${url}`, {
        headers: authHeaders()
      });
      setter(res.data);
    } catch (err) {
      if (err.response?.status !== 404) Error(err.response?.data?.errorMessage || err.message);
    }
  };

  const fetchAll = async () => {
    await Promise.all([
      load(`/ticketGroups?boardID=${id}`, setGroups),
      load(`/tickets?boardID=${id}`, setTickets),
      load(`/ticketBoards`, boards => setBoard(boards.find(b => b._id === id) || null))
    ]);
    setLoading(false);
  };

  useEffect(() => {
    setLoading(true);
    fetchAll();
  }, [id]);

  const visibleGroups = groups.filter(g =>
    g.title?.toLowerCase().includes(searchText.toLowerCase())
  );

  // Keep a valid tab selected after groups are added, removed or filtered.
  useEffect(() => {
    if (!visibleGroups.some(g => g._id === activeKey)) setActiveKey(visibleGroups[0]?._id);
  }, [groups, searchText]);

  const totalBoardTime = tickets.reduce((acc, curr) => acc + (curr.totalWorkTime || 0), 0);
  const totalEmployees = new Set(
    tickets.flatMap(ticket => (ticket.teamDetails || []).map(member => String(member.id)))
  ).size;
  const activeTickets = tickets.filter(ticket => ticket.dynamicStatus === "In Progress").length;

  const stats = [
    {
      key: "time",
      icon: <ClockCircleOutlined />,
      value: formatTime(totalBoardTime),
      label: t("Productivity"),
      color: token.colorPrimary,
      bg: token.colorPrimaryBg
    },
    {
      key: "active",
      icon: <RocketOutlined />,
      value: activeTickets,
      label: t("Active Tickets"),
      color: token.colorWarning,
      bg: token.colorWarningBg
    },
    {
      key: "people",
      icon: <TeamOutlined />,
      value: totalEmployees,
      label: t("Resources"),
      color: token.colorSuccess,
      bg: token.colorSuccessBg
    }
  ];

  const tabItems = visibleGroups.map(group => {
    const nestedTickets = tickets.filter(ticket => ticket.groupID === group._id);
    return {
      key: group._id,
      label: (
        <span className="tkt-tab-label">
          {group.title} <Text type="secondary">({nestedTickets.length})</Text>
        </span>
      ),
      children: (
        <GeneralTicketTable
          updateParent={fetchAll}
          title={group.title}
          fields={group.fields}
          group={group}
          groupId={group._id}
          Alldata={nestedTickets}
        />
      )
    };
  });

  return (
    <div className="tkt-page">
      {/* ── HERO HEADER ── */}
      <div className="tkt-hero">
        <Button
          type="link"
          className="tkt-hero-back"
          icon={<ArrowLeftOutlined />}
          onClick={() => history.push(boardsPath)}
        >
          {t("Workspaces")}
        </Button>
        <h1 className="tkt-hero-title">{board?.title || t("Dashboard")}</h1>
        <p className="tkt-hero-subtitle">{t("Tracking tickets & team performance")}</p>
      </div>

      {/* ── SEARCH + NEW GROUP ── */}
      <PageToolbar
        filters={
          <Input
            prefix={<SearchOutlined />}
            placeholder={t("Search workspace groups…")}
            aria-label={t("Search workspace groups…")}
            onChange={e => setSearchText(e.target.value)}
            allowClear
          />
        }
        actions={<AddTicket updateParent={fetchAll} />}
      />

      {/* ── STATS ── */}
      <div className="tkt-stats-row">
        {stats.map(stat => (
          <div key={stat.key} className="tkt-stat-card">
            <div className="tkt-stat-icon" style={{ background: stat.bg, color: stat.color }}>
              {stat.icon}
            </div>
            <div className="tkt-stat-info">
              <div className="tkt-stat-value">{stat.value}</div>
              <div className="tkt-stat-label">{stat.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* ── WORKSPACE GROUPS ── */}
      <div>
        <h2 className="tkt-section-title">{t("Workspace Groups")}</h2>
        <div className="tkt-groups">
          {visibleGroups.length === 0 && !loading ? (
            <div className="tkt-empty tkt-empty--large">
              <RocketOutlined className="tkt-empty__icon" />
              <p>{t("No groups yet. Add one to get started!")}</p>
            </div>
          ) : (
            <Tabs activeKey={activeKey} onChange={setActiveKey} items={tabItems} />
          )}
        </div>
      </div>

      {/* ── LOADING OVERLAY ── */}
      {loading && (
        <div className="tkt-spinner-overlay">
          <Spin size="large" />
        </div>
      )}
    </div>
  );
};

export default Tickets;
